import React, { useState } from 'react';
import { LivePage, FilterBar, DataTable, Age, Code, assertExecOk, clustersAPI , useNamespaces, NamespaceSelect } from './LivePage';
import WorkloadDetailDrawer from '../components/WorkloadDetailDrawer';
import { useCluster } from '../context/ClusterContext';
import { useAuth } from '../context/AuthContext';
import ActionsMenu, { MENU_ITEM } from '../components/ActionsMenu';

const lbl = (text) => <label style={{ fontSize:12, fontWeight:600, color:'var(--ink-2)', display:'block', marginBottom:4 }}>{text}</label>;

const newPath = () => ({ path: '/', service_name: '', service_port: '80' });
const newRule = () => ({ host: '', tls: false, tls_secret: '', paths: [newPath()] });

// 2-space YAML indent per nesting level; lines are built up by hand below rather than via
// a YAML library, matching every other kubectl-apply modal in this file (ConfigMaps,
// Secrets) — keeps the output readable in "View YAML" without pulling in a dependency.
function rulesToYaml(rules) {
  return rules.map(r => {
    const paths = r.paths.map(p => `      - path: ${p.path || '/'}
        pathType: Prefix
        backend:
          service:
            name: ${p.service_name}
            port:
              number: ${p.service_port}`).join('\n');
    return `  - host: ${r.host}
    http:
      paths:
${paths}`;
  }).join('\n');
}

function tlsToYaml(rules) {
  const tlsRules = rules.filter(r => r.tls && r.host);
  if (tlsRules.length === 0) return '';
  const entries = tlsRules.map(r => {
    const secretName = (r.tls_secret && r.tls_secret.trim()) || `${r.host.replace(/[^a-z0-9-]/g, '-')}-tls`;
    return `  - hosts:
    - ${r.host}
    secretName: ${secretName}`;
  }).join('\n');
  return `\n  tls:\n${entries}`;
}

function annotationsToYaml(pairs) {
  const valid = pairs.filter(p => p.key.trim());
  if (valid.length === 0) return '';
  const lines = valid.map(p => `    ${p.key}: "${p.value.replace(/"/g, '\\"')}"`).join('\n');
  return `\n  annotations:\n${lines}`;
}

function CreateIngressModal({ clusterId, initialNamespace = 'default', namespaces = [], onClose, onCreated }) {
  const [name, setName] = useState('');
  const [namespace, setNamespace] = useState(initialNamespace);
  const [rules, setRules] = useState([newRule()]);
  // Starts with the same proxy-read-timeout this form always sent, now editable/removable
  // rather than hardcoded — a user who doesn't want it can just delete the row.
  const [annotations, setAnnotations] = useState([
    { key: 'nginx.ingress.kubernetes.io/proxy-read-timeout', value: '3600' },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const updateRule = (i, field, val) => setRules(rs => rs.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const addRule = () => setRules(rs => [...rs, newRule()]);
  const removeRule = (i) => setRules(rs => rs.filter((_, idx) => idx !== i));

  const updatePath = (ri, pi, field, val) => setRules(rs => rs.map((r, idx) =>
    idx === ri ? { ...r, paths: r.paths.map((p, pidx) => pidx === pi ? { ...p, [field]: val } : p) } : r));
  const addPath = (ri) => setRules(rs => rs.map((r, idx) => idx === ri ? { ...r, paths: [...r.paths, newPath()] } : r));
  const removePath = (ri, pi) => setRules(rs => rs.map((r, idx) =>
    idx === ri ? { ...r, paths: r.paths.filter((_, pidx) => pidx !== pi) } : r));

  const updateAnn = (i, field, val) => setAnnotations(a => a.map((x, idx) => idx === i ? { ...x, [field]: val } : x));
  const addAnn = () => setAnnotations(a => [...a, { key: '', value: '' }]);
  const removeAnn = (i) => setAnnotations(a => a.filter((_, idx) => idx !== i));

  const handleCreate = async () => {
    if (!name) { setError('Name is required'); return; }
    for (const r of rules) {
      if (!r.host) { setError('Every rule needs a host'); return; }
      for (const p of r.paths) {
        if (!p.service_name) { setError(`Host "${r.host}": every path needs a backend service name`); return; }
      }
    }
    setSaving(true); setError('');
    try {
      const yaml = `apiVersion: networking.k8s.io/v1
kind: Ingress
metadata:
  name: ${name}
  namespace: ${namespace}${annotationsToYaml(annotations)}
spec:
  ingressClassName: nginx
  rules:
${rulesToYaml(rules)}${tlsToYaml(rules)}`;

      const cmd = `kubectl create namespace ${namespace} --dry-run=client -o yaml | kubectl apply -f - 2>/dev/null; echo '${yaml}' | kubectl apply -f -`;
      const r = await clustersAPI.exec(clusterId, cmd, { section: 'networking', action: 'create' });
      const out = r.data?.output || r.data?.stdout || '';
      const err = r.data?.stderr || '';
      const combined = `${out}\n${err}`.trim();
      const ok = /(created|configured|unchanged)/.test(combined) && !/error|denied|forbidden/i.test(err);
      if (ok) { onCreated(); onClose(); }
      else setError(combined || 'Create failed (no output from cluster — is the cluster agent connected?)');
    } catch (e) { setError(e.response?.data?.detail || 'Create failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="card" style={{ width:640, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:700 }}>Create Ingress</h2>
          <button onClick={onClose} className="btn btn-ghost btn-icon" style={{ fontSize:20 }}>✕</button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>{lbl('Name *')}<input placeholder="my-ingress" value={name} onChange={e=>setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-'))} className="input" /></div>
            <div>{lbl('Namespace')}<NamespaceSelect namespaces={namespaces} ns={namespace} onChange={setNamespace} /></div>
          </div>

          <div>
            {lbl('Hosts & Endpoints')}
            <div style={{ fontSize:11.5, color:'var(--muted)', marginBottom:8 }}>
              Each host can route multiple paths to different backend services — add another
              host for a separate domain, or another path under a host for a second endpoint
              on the same domain (e.g. /api and /app).
            </div>
            {rules.map((r, ri) => (
              <div key={ri} style={{ border:'1px solid var(--border)', borderRadius:7, padding:10, marginBottom:8 }}>
                <div style={{ display:'flex', gap:8, alignItems:'flex-end', marginBottom:8 }}>
                  <div style={{ flex:1 }}>
                    {lbl('Host (domain) *')}
                    <input placeholder="myapp.example.com" value={r.host}
                      onChange={e=>updateRule(ri,'host',e.target.value)} className="input" />
                  </div>
                  <button onClick={()=>removeRule(ri)} disabled={rules.length===1}
                    className="btn btn-ghost btn-icon btn-sm" style={{ color:'var(--color-danger-500)', fontSize:16 }}
                    title="Remove this host">×</button>
                </div>

                <div style={{ border:'1px solid var(--border)', borderRadius:7, overflow:'hidden', marginBottom:8 }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 32px', background:'var(--surface-2)', padding:'6px 10px', fontSize:11, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                    <span>Path</span><span>Backend Service</span><span>Port</span><span></span>
                  </div>
                  {r.paths.map((p, pi) => (
                    <div key={pi} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 32px', gap:4, padding:'6px 10px', borderTop:'1px solid var(--border)', alignItems:'center' }}>
                      <input placeholder="/" value={p.path} onChange={e=>updatePath(ri,pi,'path',e.target.value)} className="input" style={{ fontSize:12 }} />
                      <input placeholder="my-service" value={p.service_name} onChange={e=>updatePath(ri,pi,'service_name',e.target.value)} className="input" style={{ fontSize:12 }} />
                      <input type="number" value={p.service_port} onChange={e=>updatePath(ri,pi,'service_port',e.target.value)} className="input" style={{ fontSize:12 }} />
                      <button onClick={()=>removePath(ri,pi)} disabled={r.paths.length===1}
                        className="btn btn-ghost btn-icon btn-sm" style={{ color:'var(--color-danger-500)', fontSize:16 }}>×</button>
                    </div>
                  ))}
                  <div style={{ padding:'6px 10px', borderTop:'1px solid var(--border)' }}>
                    <button onClick={()=>addPath(ri)} className="btn btn-link">+ Add path</button>
                  </div>
                </div>

                <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
                  <input type="checkbox" checked={r.tls} onChange={e=>updateRule(ri,'tls',e.target.checked)} />
                  Enable TLS for this host
                </label>
                {r.tls && (
                  <div style={{ marginTop:6 }}>
                    <input
                      placeholder={r.host ? `${r.host.replace(/[^a-z0-9-]/g,'-')}-tls` : 'existing-tls-secret'}
                      value={r.tls_secret}
                      onChange={e=>updateRule(ri,'tls_secret', e.target.value.toLowerCase().replace(/[^a-z0-9-.]/g,'-'))}
                      className="input" style={{ fontSize:12 }} />
                    <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>
                      Name of an <strong>existing</strong> TLS secret (kubernetes.io/tls) in this
                      namespace. Leave blank to default to the host name + <code>-tls</code>.
                      Create the secret first in Configuration → Secrets.
                    </div>
                  </div>
                )}
              </div>
            ))}
            <button onClick={addRule} className="btn btn-link">+ Add another host</button>
          </div>

          <div>
            {lbl('Annotations')}
            <div style={{ fontSize:11.5, color:'var(--muted)', marginBottom:6 }}>
              Controller-specific behaviour (timeouts, rewrite rules, rate limits, etc.) —
              <code> nginx.ingress.kubernetes.io/*</code> for the nginx controller.
            </div>
            <div style={{ border:'1px solid var(--border)', borderRadius:7, overflow:'hidden' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 32px', background:'var(--surface-2)', padding:'6px 10px', fontSize:11, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                <span>Key</span><span>Value</span><span></span>
              </div>
              {annotations.map((a, i) => (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 32px', gap:4, padding:'6px 10px', borderTop:'1px solid var(--border)', alignItems:'center' }}>
                  <input placeholder="nginx.ingress.kubernetes.io/..." value={a.key}
                    onChange={e=>updateAnn(i,'key',e.target.value)} className="input" style={{ fontFamily:'monospace', fontSize:11.5 }} />
                  <input placeholder="value" value={a.value}
                    onChange={e=>updateAnn(i,'value',e.target.value)} className="input" style={{ fontSize:12 }} />
                  <button onClick={()=>removeAnn(i)} className="btn btn-ghost btn-icon btn-sm" style={{ color:'var(--color-danger-500)', fontSize:16 }}>×</button>
                </div>
              ))}
              <div style={{ padding:'6px 10px', borderTop:'1px solid var(--border)' }}>
                <button onClick={addAnn} className="btn btn-link">+ Add annotation</button>
              </div>
            </div>
          </div>

          {error && <div className="banner danger">{error}</div>}
          <div style={{ display:'flex', gap:8, marginTop:4 }}>
            <button onClick={handleCreate} disabled={saving} className="btn btn-primary" style={{ flex:1 }}>
              {saving ? '⟳ Creating...' : '+ Create Ingress'}
            </button>
            <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Ingresses() {
  const { hasSectionAction } = useAuth();
  const { selectedCluster } = useCluster();
  // SARVKUBE-65: the cluster's REAL namespace list, so the dropdown offers namespaces whose
  // objects are not currently displayed. Built from loaded rows before, which made it
  // impossible to reach a namespace that was not already on screen.
  const { namespaces: allNamespaces } = useNamespaces(selectedCluster?.id);
  const canCreate = hasSectionAction('networking', 'create');
  const canDelete = hasSectionAction('networking', 'delete');
  const [nsFilter, setNsFilter]     = useState('all');
  const [nameFilter, setNameFilter] = useState('');
  // Describe + edit for one object, in the shared drawer.
  const [detailFor, setDetailFor] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <>
    <LivePage deps={[nsFilter]} title="Ingresses" fetchFn={id => clustersAPI.getWorkloadPaged('ingresses', id, { pageSize: 500, namespace: nsFilter === 'all' ? undefined : nsFilter })}>
      {(data, refresh, clusterId) => {
        // API returns a raw K8s List ({items:[Service|Ingress...]}); normalize
        // Ingress items into the flat shape the table expects. (Was
        // `data.items || data.ingresses || []` -> always empty.)
        const _raw = data.items || data.ingresses || [];
        const items = _raw
          .filter(it => (it.kind || 'Ingress') === 'Ingress')
          .map(it => {
            if (it.metadata) {
              const rules = (it.spec?.rules || []);
              return {
                name: it.metadata.name,
                namespace: it.metadata.namespace,
                hosts: rules.map(r => r.host).filter(Boolean).join(', '),
                class: it.spec?.ingressClassName || (it.metadata.annotations || {})['kubernetes.io/ingress.class'] || '',
                created_at: it.metadata.creationTimestamp,
              };
            }
            return it;
          });
        const namespaces = ['all', ...allNamespaces].sort();   // cluster-wide, not just loaded rows
        const filtered = items.filter(i =>
          (nsFilter === 'all' || i.namespace === nsFilter) &&
          i.name.toLowerCase().includes(nameFilter.toLowerCase())
        );

        const handleDelete = async (name, namespace) => {
          if (!window.confirm(`Delete ingress "${name}" in "${namespace}"?`)) return;
          try {
            const r = await clustersAPI.exec(clusterId, `kubectl delete ingress ${name} -n ${namespace} --ignore-not-found=true`, { section: 'networking', action: 'delete' });
            assertExecOk(r, 'Delete');   // kubectl failures come back HTTP 200 on stderr
            refresh();
          } catch (e) { alert(`Delete failed: ${e.response?.data?.detail || e.message}`); }
        };

        return <>
          <FilterBar count={filtered.length} total={items.length} label="ingresses">
            <select value={nsFilter} onChange={e => setNsFilter(e.target.value)}>
              {namespaces.map(ns => <option key={ns} value={ns}>{ns === 'all' ? 'All namespaces' : ns}</option>)}
            </select>
            <input placeholder="Filter by name..." value={nameFilter} onChange={e => setNameFilter(e.target.value)} />
            {canCreate && (
            <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm" style={{ marginLeft:'auto' }}>
              + Create Ingress
            </button>
            )}
          </FilterBar>
          <DataTable cols={['Name', 'Namespace', 'Hosts', 'Class', 'Age', '']}
            rows={filtered.map(i => (
              <tr key={`${i.namespace}/${i.name}`}>
                <td><Code>{i.name}</Code></td>
                <td style={{ color: 'var(--ink-2)', fontSize: 13 }}>{i.namespace}</td>
                <td style={{ fontSize: 12 }}>{i.hosts || '—'}</td>
                <td style={{ fontSize: 12 }}>{i.class || '—'}</td>
                <td><Age ts={i.created_at} /></td>
                <td>
                  <ActionsMenu>
                    <button onClick={() => setDetailFor({ namespace: i.namespace, name: i.name })}
                      style={MENU_ITEM} title="Selectors, ports, routes, events and editable YAML">
                      Details
                    </button>
                    {canDelete && (
                    <button onClick={() => handleDelete(i.name, i.namespace)} style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }}>
                      Delete
                    </button>
                    )}
                  </ActionsMenu>
                </td>
              </tr>
            ))} />
          {showCreate && (
            <CreateIngressModal
              clusterId={clusterId}
              initialNamespace={nsFilter !== 'all' ? nsFilter : 'default'}
              namespaces={allNamespaces}
              onClose={() => setShowCreate(false)}
              onCreated={() => { setShowCreate(false); refresh(); }}
            />
          )}
        </>;
      }}
    </LivePage>
    {detailFor && selectedCluster && (
      <WorkloadDetailDrawer
        clusterId={selectedCluster.id}
        kind="Ingress"
        resource="ingresses"
        namespace={detailFor.namespace}
        name={detailFor.name}
        onClose={() => setDetailFor(null)}
      />
    )}
    </>
  );
}
